import React from 'react';
import { PlayIcon } from '@phosphor-icons/react';

import EmbedConsentGate from 'volto-ministerio-publico-federal/components/CookieConsent/EmbedConsentGate';
import { youtubeEmbedSrc, type VideoCard } from './helpers';
import { comPrefixo } from 'volto-ministerio-publico-federal/helpers/Url/subpath';

interface VideoPrincipalProps {
  card: VideoCard;
  /** No editor o clique não deve trocar a tela por um iframe de terceiro. */
  isEditMode?: boolean;
}

/**
 * Player do vídeo em cartaz, em duas fases.
 *
 * Fase 1 (fachada): só a miniatura do YouTube e um botão de play. Nenhum
 * `<iframe>` no DOM significa nenhum script do Google e nenhum cookie de
 * rastreio para quem apenas passou pela página — e uma requisição a menos no
 * carregamento, que é o gargalo desta faixa.
 *
 * Fase 2: o `<iframe>` com `autoplay`, montado só depois do clique.
 *
 * Quem troca o vídeo em cartaz (a lista lateral) remonta este componente com
 * uma `key` nova. É a remontagem que devolve a fachada e, de quebra, faz o
 * áudio do vídeo anterior parar — desmontar o iframe é a única forma de parar
 * um vídeo do YouTube sem a API dele (mesma mecânica anotada em
 * `View/CapaView/YoutubeDialog.tsx`).
 */
export const VideoPrincipal: React.FC<VideoPrincipalProps> = ({
  card,
  isEditMode,
}) => {
  const [tocando, setTocando] = React.useState(false);
  const [thumbQuebrada, setThumbQuebrada] = React.useState(false);

  const rotulo = card.titulo
    ? `Assistir ao vídeo: ${card.titulo}`
    : 'Assistir ao vídeo';

  return (
    <div className="mpf-videos__principal">
      <div className="mpf-videos__palco">
        {tocando ? (
          <EmbedConsentGate
            provider="YouTube"
            href={`https://www.youtube.com/watch?v=${card.videoId}`}
          >
            <iframe
              className="mpf-videos__iframe"
              src={youtubeEmbedSrc(card.videoId)}
              title={card.titulo || 'Vídeo'}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </EmbedConsentGate>
        ) : (
          <button
            type="button"
            className="mpf-videos__fachada"
            aria-label={rotulo}
            title={rotulo}
            onClick={() => !isEditMode && setTocando(true)}
          >
            <img
              className="mpf-videos__thumb"
              src={comPrefixo(thumbQuebrada ? card.thumbFallback : card.thumb)}
              alt=""
              loading="lazy"
              onError={() => setThumbQuebrada(true)}
            />
            <span className="mpf-videos__play mpf-videos__play--grande">
              <PlayIcon weight="fill" aria-hidden="true" />
            </span>
          </button>
        )}
      </div>

      {(card.titulo || card.descricao || card.transcricao) && (
        <div className="mpf-videos__legenda">
          {card.titulo && (
            <p className="mpf-videos__legenda-titulo">{card.titulo}</p>
          )}
          {card.descricao && (
            <p className="mpf-videos__legenda-texto">{card.descricao}</p>
          )}
          {/* `<details>` nativo: abre pelo teclado e o leitor de tela anuncia
              o estado, sem JS. O texto fica no DOM mesmo fechado. */}
          {card.transcricao && (
            <details className="mpf-videos__transcricao">
              <summary className="mpf-videos__transcricao-titulo">
                Transcrição do vídeo
              </summary>
              <div className="mpf-videos__transcricao-texto">
                {card.transcricao.split(/\n{2,}/).map((paragrafo, index) => (
                  <p key={index}>{paragrafo}</p>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoPrincipal;
